/* globals $ */

import PropTypes from "prop-types";
import React from "react";
import CSSTransitionGroup from "react-transition-group/CSSTransitionGroup";
import { GoogleReCaptcha } from "react-google-recaptcha-v3";
import queryString from "query-string";

import ClientStats from "shared/util/ClientStats";
import isApp from "shared/util/isApp";
import _str from "shared/util/_str";
import { locationPropTypes, historyPropTypes } from "shared/constants/propTypes";
import { CONFIRM_RECAPTCHA_SUCCESS } from "ducks/login_old";

import DesktopLoggedoutHeader from "shared/components/DesktopLoggedoutHeader";
import Header from "apps/login_old/components/Header/";
import Login from "apps/login_old/components/screens/Login";
import PasswordReset from "apps/login_old/components/screens/PasswordReset";
import PasswordSubmit from "apps/login_old/components/screens/PasswordSubmit";
import Reactivate from "apps/login_old/components/screens/Reactivate";
import LoginCaptcha from "apps/login_old/components/screens/LoginCaptcha";
import FeedbackBanner from "shared/util/FeedbackBanner";

import { SCREENS, SCREEN_SPEED, DIRECT_LINKABLE_SCREENS } from "apps/login_old/util/constants";

import "./App.scss";

if (process.env.PLATFORM === "desktop") {
	require("./App.desktop.scss");
} else {
	require("./App.mobile.scss");
}

class App extends React.Component {
	static propTypes = {
		screen: PropTypes.string.isRequired,
		loadLogin: PropTypes.func.isRequired,
		submitLogin: PropTypes.func.isRequired,
		reactivateAccount: PropTypes.func.isRequired,
		facebookLogin: PropTypes.func.isRequired,
		submitReset: PropTypes.func.isRequired,
		changeScreen: PropTypes.func.isRequired,
		showFacebookLogin: PropTypes.bool,
		showTwoFactor: PropTypes.bool.isRequired,
		isSubmitting: PropTypes.bool.isRequired,
		isLoading: PropTypes.bool.isRequired,
		isLoggedIn: PropTypes.bool.isRequired,
		error: PropTypes.shape({
			type: PropTypes.node,
			message: PropTypes.node,
		}),
		confirmRecaptchaToken: PropTypes.func.isRequired,
		location: locationPropTypes.isRequired,
		history: historyPropTypes.isRequired,
	};

	static defaultProps = {
		showFacebookLogin: false,
		error: null,
	};

	state = {
		username: "",
		password: "",
		captchaKey: null,
		authcode: "",
		saveAuth: false,
	};

	componentDidMount() {
		const { loadLogin, screen, location, changeScreen } = this.props;
		const step = queryString.parse(location.search).step;

		ClientStats.update({
			statname: `login - onboarding funnel - view:${screen}`,
			unique: true,
		});

		loadLogin();

		if (isApp()) {
			$(this.container).immobilize(true, true);
		}

		if (step === DIRECT_LINKABLE_SCREENS.PASSWORD_RESET) {
			changeScreen(SCREENS.PASSWORD_RESET);
		}
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.error && nextProps.error !== this.props.error) {
			FeedbackBanner.open({
				message: nextProps.error.message,
				type: "error",
				timeout: 3000,
			});
		}

		if (nextProps.screen !== this.props.screen) {
			ClientStats.update({
				statname: `login - onboarding funnel - view:${nextProps.screen}`,
				unique: true,
			});
		}
	}

	_changeField = (field, value) => {
		this.setState({ [field]: value });
	};

	_onChangeUsername = (ev) => {
		this.setState({ username: ev.target.value });
	};

	_onChangePassword = (ev) => {
		this.setState({ password: ev.target.value });
	};

	_submitReset = (email) => {
		const { location, history } = this.props;

		// remove the search from the url to prevent the reset password screen from staying rendered
		// since ResetPassword is always rendered if the query step=reset_password
		const nextSearch = queryString.stringify({
			...queryString.parse(location.search),
			step: undefined,
		});

		history.replace({
			pathname: location.pathName,
			search: nextSearch,
		});

		this.props.submitReset(email);
	};

	_submitLogin = () => {
		// Uses what's in state, so no args here.
		this.props.submitLogin({
			username: this.state.username,
			password: this.state.password,
			captchaKey: this.state.captchaKey,
			authcode: this.state.authcode,
			saveAuth: this.state.saveAuth,
		});
	};

	_reactivateAccount = () => {
		this.props.reactivateAccount();
	};

	_facebookLogin = () => {
		this.props.facebookLogin();
	};

	_onCaptchaVerify = (captchaKey) => {
		this.setState({ captchaKey });
	}

	_onVerifyCaptchaTrackEvent = (captchaKey) => {
		const {
			confirmRecaptchaToken,
			screen,
		} = this.props;

		confirmRecaptchaToken({
			type: CONFIRM_RECAPTCHA_SUCCESS,
			token: captchaKey,
			isCaptchaShowing: screen === SCREENS.CAPTCHA,
			page: "login",
		});
	}

	_onSubmit = (ev) => {
		ev.preventDefault();
		this.props.submitLogin(this.state.username, this.state.password);
	};

	render() {
		const {
			screen,
			changeScreen,
			showFacebookLogin,
			showTwoFactor,
			isSubmitting,
			isLoading,
			isLoggedIn,
			location,
		} = this.props;
		const {
			username,
			password,
			authcode,
			saveAuth,
		} = this.state;

		const step = queryString.parse(location.search).step;
		const skipToResetPassword = step === DIRECT_LINKABLE_SCREENS.PASSWORD_RESET;

		let screenEl;
		let screenTitle;

		// check if the query has reset_password to prevent rendering login and then reset password
		// once the screen is updated in componentDidMount
		// skipToResetPassword is used by web view via a direct link to PASSWORD_RESET
		if (screen === SCREENS.PASSWORD_RESET || skipToResetPassword) {
			screenTitle = _str("Forgot password");
			screenEl = (
				<PasswordReset
					submitReset={this._submitReset}
					changeScreen={changeScreen}
					username={username}
					isLoading={isLoading}
				/>
			);
		} else if (screen === SCREENS.LOGIN) {
			screenTitle = _str("Sign in");
			screenEl = (
				<Login
					submitLogin={this._submitLogin}
					showFacebookLogin={!!showFacebookLogin}
					facebookLogin={this._facebookLogin}
					changeField={this._changeField}
					changeScreen={changeScreen}
					username={username}
					password={password}
					isSubmitting={isSubmitting}
					isLoggedIn={isLoggedIn}
					showTwoFactor={showTwoFactor}
					authcode={authcode}
					saveAuth={saveAuth}
				/>
			);
		} else if (screen === SCREENS.PASSWORD_SUBMIT) {
			if (process.env.PLATFORM === "desktop") {
				screenTitle = _str("Forgot password");
			}

			screenEl = (
				<PasswordSubmit changeScreen={changeScreen} />
			);
		} else if (screen === SCREENS.REACTIVATE) {
			if (process.env.PLATFORM === "desktop") {
				screenTitle = _str("Howdy");
			}

			screenEl = (
				<Reactivate submitLogin={this._reactivateAccount} />
			);
		} else if (screen === SCREENS.CAPTCHA) {
			screenTitle = _str("Sign in");
			screenEl = (
				<LoginCaptcha
					onSignIn={this._submitLogin}
					onCaptchaVerify={this._onCaptchaVerify}
					isLoggingIn={isSubmitting}
					isLoggedIn={isLoggedIn}
				/>
			);
		}

		return (
			<div className="login2017" ref={(el) => { this.container = el; }}>
				{process.env.PLATFORM === "desktop" &&
					<DesktopLoggedoutHeader />
				}

				<div className="login2017-container">
					<Header
						screen={screen}
						changeScreen={changeScreen}
						title={screenTitle}
					/>

					<CSSTransitionGroup
						transitionName="loginscreen-transition"
						transitionEnterTimeout={SCREEN_SPEED}
						transitionLeaveTimeout={SCREEN_SPEED}
					>
						<div className="loginscreen2017" key={screen}>
							{screenEl}
						</div>
					</CSSTransitionGroup>
				</div>
				<GoogleReCaptcha
					onVerify={this._onVerifyCaptchaTrackEvent}
					action="login"
				/>
			</div>
		);
	}
}

export default App;



// WEBPACK FOOTER //
// ./src/apps/login_old/components/App.jsx